import { CheckBadgeIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import cn from 'classnames';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ChallengeQuestions from '../../../components/ChallengeQuestions';
import { useApplicationStore } from './applicationStore';

const SecureNewClient = ({
	showSkip = true,
	onValidated = () => {},
	updateAppStore = false,
}) => {
	const {
		setChallengeResponses,
		challengeResponses,
		setUsername,
		setLast4SSN,
		skipAccountCreation,
		setSkipAccountCreation,
	} = useApplicationStore();
	const [pin, setPin] = useState('');
	const [confirmPin, setConfirmPin] = useState('');
	const [usernameLocal, setUsernameLocal] = useState('');
	const usernameQuery = useQuery({
		queryKey: ['username', usernameLocal],
		queryFn: async () => {
			const { data } = await axios.get('/requests/check-username', {
				params: { username: usernameLocal },
			});
			return data;
		},
		enabled: false,
	});
	const questionsQuery = useQuery({
		queryKey: ['questions'],
		queryFn: async () => {
			const { data } = await axios.get('/clients/security-questions');
			return data;
		},
	});
	useEffect(() => {
		let isPinValid = false;
		let isUsernameValid = false;
		let isChallengeResponsesValid = false;
		if (!isEmpty(pin) && pin.length === 4 && pin === confirmPin) {
			// setLast4SSN(pin);
			isPinValid = true;
		}
		if (!isEmpty(usernameLocal) && !usernameQuery.data?.exists) {
			// setUsername(usernameLocal);
			isUsernameValid = true;
		}
		if (challengeResponses?.length > 2) {
			isChallengeResponsesValid = challengeResponses.every(
				(response) =>
					!isEmpty(response.question_id) && !isEmpty(response.answer),
			);
		}
		const isValid = isPinValid && isUsernameValid && isChallengeResponsesValid;
		onValidated(isValid);
	}, [pin, confirmPin, usernameLocal, usernameQuery, challengeResponses]);
	useEffect(() => {
		if (updateAppStore) {
			setLast4SSN(pin);
			setUsername(usernameLocal);
		}
	}, [updateAppStore]);

	const handleCheckUsername = () => {
		usernameQuery.refetch();
	};

	const handleSkipChanged = ({ target }) => {
		setUsername('');
		setChallengeResponses(null);
		setLast4SSN(undefined);
		setSkipAccountCreation(target.checked);
	};

	const pinsAreMismatched =
		!isEmpty(pin) && !isEmpty(confirmPin) && pin !== confirmPin;

	return (
		<>
			<p>
				<em>
					Choose a username, 4-digit verification number, and answer challenge
					questions. Choices are only saved after a referral is created.
				</em>
			</p>
			<div className="secure-new-client">
				{showSkip && (
					<div className="form-control">
						<label className="gap-4 cursor-pointer label justify-normal">
							<input
								type="checkbox"
								checked={skipAccountCreation}
								className="checkbox"
								onChange={handleSkipChanged}
							/>
							<span className="label-text">Skip account creation</span>
						</label>
					</div>
				)}
				<div className="form-control">
					<label className="label label-text" htmlFor="username">
						Username
					</label>
					<input
						type="text"
						name="username"
						id="username"
						autoComplete="off"
						className={cn('input', 'input-bordered', {
							'input-error': usernameQuery.data?.exists,
						})}
						onChange={({ target }) => setUsernameLocal(target.value)}
						value={usernameLocal}
						required
						onBlur={handleCheckUsername}
						disabled={skipAccountCreation}
					/>
					{usernameQuery.isFetched && (
						<>
							{usernameQuery.data.exists ? (
								<div className="label label-text text-error">
									<span>
										<XCircleIcon className="w-4 h-4 text-error" /> Username is
										not available
									</span>
								</div>
							) : (
								<div className="label label-text">
									<span>
										<CheckBadgeIcon className="w-4 h-4 text-success" /> Username
										is available
									</span>
								</div>
							)}
						</>
					)}
				</div>
				<div className="grid grid-cols-2 gap-4">
					<div className="form-control">
						<label className="label label-text" htmlFor="pin">
							4 digit verification number
						</label>
						<input
							type="password"
							name="pin"
							id="pin"
							className="input input-bordered"
							maxLength={4}
							pattern="\d{4}"
							required
							disabled={skipAccountCreation}
							value={pin}
							onChange={({ target }) => setPin(target.value)}
						/>
					</div>
					<div className="form-control">
						<label className="label label-text" htmlFor="confirm-pin">
							Confirm verification number
						</label>
						<input
							type="password"
							name="confirm-pin"
							id="confirm-pin"
							className="input input-bordered"
							maxLength={4}
							pattern="\d{4}"
							required
							disabled={skipAccountCreation}
							value={confirmPin}
							onChange={({ target }) => setConfirmPin(target.value)}
						/>
					</div>
					{pinsAreMismatched && (
						<div className="col-span-2 text-error">
							<span>
								<XCircleIcon className="inline-block w-4 h-4 text-error" /> PINs
								do not match!
							</span>
						</div>
					)}
				</div>
				<ChallengeQuestions
					questions={questionsQuery.data}
					onResponsesChange={setChallengeResponses}
					disabled={skipAccountCreation}
				/>
			</div>
		</>
	);
};

SecureNewClient.propTypes = {
	showSkip: PropTypes.bool,
	onValidated: PropTypes.func,
	updateAppStore: PropTypes.bool,
};

export default SecureNewClient;
